import { Component } from '@angular/core';
import { AuthService } from '../../Services/Auth/auth.service';
import { UserProfileData } from '../../interfaces/user-profile-data';
import { Router } from '@angular/router';
import { combineLatest, distinctUntilChanged, Subject, switchMap, take, takeUntil } from 'rxjs';
import { CanActivateService } from '../../Services/CanActivate/can-activate.service';
import { BackendAccessService } from '../../Services/APICalls/backend-access-service';
import { PreconfiguredSub } from '../../interfaces/preconfiguredSubs.interface';
import { PreconfigSubServiceService } from '../../Services/preconfig-sub-service.service';
import { LoadingService } from '../../Services/Loading/loading-service.service';
import { MaxioCustomerService } from '../../Services/maxio-customer.service';

@Component({
  selector: 'app-almhome',
  templateUrl: './almhome.component.html',
  styleUrls: ['./almhome.component.scss'],
  standalone: false,
})
export class ALMHomeComponent {
  userProfile: UserProfileData | null = null;
  showCard = true;
  isTechSupportInternalUser!: boolean;
  isInternalAdminUser!: boolean;
  isExternalUser!: boolean;
  isTechSupportExternalUser!: boolean;
  isZentraEndUser!: boolean;
  isCredLibraryUser!: boolean;
  companyAccountId: string | undefined;
  hasCompany!: boolean;
  isInternalUser!: boolean;
  unsubscribe$ = new Subject<void>();
  preconfiguredSub: PreconfiguredSub[] = [];
  userPermissions: string[] = [];
  activeSubs: any[] = [];
  isCompanyAdmin!: boolean;
  isZentraWholesaler!: boolean;

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly canAct: CanActivateService,
    readonly backend: BackendAccessService,
    readonly preconfigSubService: PreconfigSubServiceService,
    readonly loading: LoadingService,
    private readonly maxioCustomerService: MaxioCustomerService
  ) {}

  /* istanbul ignore next */
  ngOnInit(): void {
    this.loading.show();

    // Combine profile and permissions observables
    combineLatest([
      this.canAct.companyProfile$.pipe(takeUntil(this.unsubscribe$)),
      this.authService.userProfile$.pipe(takeUntil(this.unsubscribe$)),
      this.authService.getPermissions().pipe(
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        ),
        takeUntil(this.unsubscribe$)
      ),
      this.authService.getAllRoles().pipe(takeUntil(this.unsubscribe$)),
    ]).subscribe(([profileData, userProfile, permissions, roles]) => {
      this.handleCompanyProfile(profileData);
      this.handleUserProfile(userProfile);
      this.handlePermissions(permissions);
      // ZentraIntegrator/ZentraWholesaler are derived from backend permissions,
      // not the Auth0 profile claims, so they come from getAllRoles() instead.
      this.isZentraWholesaler = roles.includes('ZentraWholesaler');
    });
  }
  /* istanbul ignore next */
  onReviewClick(sub: PreconfiguredSub): void {
    console.log('Review clicked for subscription!!!:', sub);
    // Wait for customer data to be fetched before navigating
    this.onCreateSubscription().then(() => {
      console.log(
        'Navigating to Zentra Subscription page for recordId:',
        sub.recordId
      );
      this.router.navigate(['/zentra-subscription', sub.recordId]);
    });
  }
  /* istanbul ignore next */
  onCreateSubscription(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.authService
        .getUser$()
        .pipe(
          take(1),
          switchMap((user) => {
            const email = user?.email;
            if (!email) {
              // handle missing email case, maybe return empty observable or throw error
              throw new Error('User email not found');
            }
            return this.backend.getMaxioCustomerList(email);
          }),
          take(1)
        )
        .subscribe({
          next: (customers) => {
            if (customers && customers.length > 0) {
              this.maxioCustomerService.setCustomerData(customers[0]);
            } else {
              this.maxioCustomerService.setCustomerData(null);
            }
            resolve();
          },
          error: (err) => {
            console.error('Error fetching Maxio customers', err);
            this.maxioCustomerService.setCustomerData(null);
            resolve(); // Resolve anyway so navigation continues
          },
        });
    });
  }

  /* istanbul ignore next */
  private handleCompanyProfile(profileData: any): void {
    if (profileData) {
      this.hasCompany = profileData.companyName?.trim() !== '';
      this.isCompanyAdmin = !!profileData.isCompanyAdmin;
    }
  }
  /* istanbul ignore next */
  private handleUserProfile(profile: any): void {
    if (profile) {
      this.userProfile = profile;
      this.isInternalUser =
        profile['http://allegion.com/claims/user_type'] === 'internal';
      this.isExternalUser =
        profile['http://allegion.com/claims/user_type'] === 'external';
      const roles = profile['http://allegion.com/claims/roles'] ?? [];
      this.isZentraEndUser = roles.includes('ZentraEndUser');
      this.isCredLibraryUser = this.checkCredLibraryUser(roles);
      this.isInternalAdminUser = this.checkInternalAdminUser(roles);
      this.isTechSupportInternalUser = this.checkTechSupportInternalUser(roles);
      this.isTechSupportExternalUser = this.checkTechSupportExternalUser(roles);
    }
  }

  ngOnDestroy(): void {
    // Trigger unsubscribe
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private lastFetchedPermissionsKey: string | null = null;

  /* istanbul ignore next */
  private handlePermissions(permissions: any): void {
    this.userPermissions = permissions;
    console.log('User Permissions:', this.userPermissions);

    // combineLatest re-runs this whenever ANY of its sources emit (e.g. the
    // user profile going from its initial null to the real value), even when
    // permissions itself hasn't changed. Guard fetchSubscriptions() so it
    // doesn't redundantly re-fire the landing API call in that case.
    const permissionsKey = JSON.stringify(permissions);
    if (permissionsKey === this.lastFetchedPermissionsKey) {
      return;
    }
    this.lastFetchedPermissionsKey = permissionsKey;

    if (
      permissions.includes('Subscription View For EndUser') ||
      permissions.includes('Subscription View For Integrator') ||
      permissions.includes('Subscription View For GuestPurchaser')
    ) {
      this.fetchSubscriptions();
    } else {
      this.loading.hide(); // Hide loading if no permission to view subscriptions
    }
  }
  /* istanbul ignore next */
  private fetchSubscriptions(): void {
    this.loading.show(); // Show loading indicator while fetching subscriptions
    this.backend.getSubConfigsActiveSubsZentraEndUser().subscribe({
      next: (response) => {
        if (response) {
          this.handlePreconfiguredSubscriptions(response.preconfigured_sub);
          this.handleActiveSubscriptions(response.active_subs);
        }
      },
      error: (error) => {
        console.error('Error fetching subscriptions:', error);
        this.loading.hide();
      },
      complete: () => {
        this.loading.hide(); // Ensure loading is hidden when request completes
      },
    });
  }
  /* istanbul ignore next */
  private handlePreconfiguredSubscriptions(preconfiguredSub: any): void {
    if (preconfiguredSub) {
      this.preconfiguredSub = preconfiguredSub;
      console.log('Preconfigured Subscriptions:', this.preconfiguredSub);
      this.preconfigSubService.setSubscriptionData(this.preconfiguredSub);
    }
  }
  /* istanbul ignore next */
  private handleActiveSubscriptions(activeSubs: any): void {
    if (activeSubs) {
      this.activeSubs = activeSubs;
      console.log('Active Subscriptions:', this.activeSubs);
    }
  }
  /* istanbul ignore next */
  private checkCredLibraryUser(roles: string[]): boolean {
    return [
      'APIUser',
      'MobileCredUser',
      'FullControl',
      'ViewOnly',
      'InternalAdmin',
    ].some((role) => roles.includes(role));
  }
  /* istanbul ignore next */
  private checkInternalAdminUser(roles: string[]): boolean {
    return (
      (this.isInternalUser && roles.includes('InternalAdmin')) ||
      roles.includes('ZentraAdmin')
    );
  }
  /* istanbul ignore next */
  private checkTechSupportInternalUser(roles: string[]): boolean {
    return (
      this.isInternalUser &&
      (roles.includes('TechSupport') || roles.includes('Onboarding'))
    );
  }
  /* istanbul ignore next */
  private checkTechSupportExternalUser(roles: string[]): boolean {
    return (
      this.isExternalUser &&
      (roles.includes('TechSupport') ||
        roles.includes('Onboarding') ||
        roles.includes('InternalAdmin') ||
        roles.includes('ZentraAdmin'))
    );
  }

  /* istanbul ignore next */
  dismissCard(): void {
    this.showCard = false; // Hide the card when the close button is clicked
  }
  /* istanbul ignore next */
  handleKeyDown(event: KeyboardEvent, targetRoute: string): void {
    // Check if the pressed key is 'Enter' or 'Space'
    if (event.key === 'Enter' || event.key === 'Space') {
      // Prevent the default action to avoid any unwanted side effects
      event.preventDefault();
      // Navigate to the specified route
      this.navigateTo(targetRoute);
    }
  }
  /* istanbul ignore next */
  navigateTo(targetRoute: string): void {
    this.router.navigateByUrl(targetRoute);
  }
  /* istanbul ignore next */
  goToSubscription(sub: any) {
    this.router.navigate(['/active-subscription'], {
      queryParams: { maxioSubId: sub.maxio_subscription_Id },
    });
  }
}
